import { useEffect, useState } from "react";
import AdminLayout from "../components/AdminLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { TrendingDown, DollarSign, MessageSquare, Quote } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer, Cell } from "recharts";
import { apiClient } from "../App";

// -----------------------------------------------------------------------------
// AdminChurnInsights — surfaces cancellation reasons + MRR loss over a
// configurable window. Every cancellation collected via
// /api/subscription/cancel lands here. Backed by GET /api/admin/churn-insights.
// -----------------------------------------------------------------------------

const RANGE_OPTIONS = [
  { value: 30, label: "Last 30 days" },
  { value: 60, label: "Last 60 days" },
  { value: 90, label: "Last 90 days" },
  { value: 180, label: "Last 6 months" },
  { value: 365, label: "Last 12 months" },
];

// Reason → bar color. Same palette as the pricing grid tier icons so
// admins can pattern-match across the dashboard.
const REASON_COLORS = {
  too_expensive: "#E11D48",       // red — biggest fix leverage
  missing_features: "#0D9488",    // teal — product roadmap signal
  switching_tools: "#8B5CF6",     // purple — competitive signal
  not_using_enough: "#F59E0B",    // amber — engagement signal
  temporary_pause: "#94A3B8",     // slate — often reversible
  closing_business: "#64748B",    // slate darker — unavoidable
  other: "#334155",               // dark slate — miscellaneous
};

const AdminChurnInsights = () => {
  const [days, setDays] = useState(90);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await apiClient.get(`/admin/churn-insights?days=${days}`);
        if (!cancelled) setData(res.data);
      } catch (e) {
        console.error("churn insights failed:", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [days]);

  const fmtMoney = (cents) => `$${((cents || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

  return (
    <AdminLayout>
      <div className="space-y-6 max-w-6xl">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-[#0A192F] font-['Manrope']">Churn Insights</h1>
            <p className="text-sm text-slate-500 mt-1">
              Why customers are cancelling and how much MRR is walking out the door.
            </p>
          </div>
          <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
            <SelectTrigger className="w-[180px]" data-testid="churn-range-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RANGE_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={String(o.value)}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Summary stat cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Card className="border-slate-200">
            <CardContent className="p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center">
                  <TrendingDown className="w-5 h-5 text-red-500" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-500 font-medium">Cancellations</p>
                  <p className="text-2xl font-bold text-[#0A192F]" data-testid="churn-total-cancellations">
                    {loading ? "—" : (data?.total_cancellations ?? 0)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-500 font-medium">MRR Lost</p>
                  <p className="text-2xl font-bold text-[#0A192F]" data-testid="churn-total-mrr">
                    {loading ? "—" : fmtMoney(data?.total_mrr_lost_cents)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Reason breakdown */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              Cancellation reasons
            </CardTitle>
            <CardDescription>
              Grouped by reason. Bar length = cancellation count; hover for MRR lost per reason.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-sm text-slate-400 text-center py-8">Loading…</p>
            ) : !data?.reasons?.length ? (
              <div className="text-center py-8 text-slate-400 text-sm" data-testid="churn-empty-state">
                <MessageSquare className="w-10 h-10 mx-auto mb-2 opacity-30" />
                No cancellations in this window — nice work.
              </div>
            ) : (
              <div className="h-[300px]" data-testid="churn-reason-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.reasons} layout="vertical" margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} stroke="#64748b" />
                    <YAxis
                      type="category"
                      dataKey="label"
                      width={180}
                      tick={{ fontSize: 12 }}
                      stroke="#64748b"
                    />
                    <RTooltip
                      formatter={(value, name, props) => [
                        `${value} cancellation${value === 1 ? "" : "s"} · ${fmtMoney(props.payload.mrr_lost_cents)} MRR`,
                        "",
                      ]}
                      labelStyle={{ fontWeight: 600 }}
                      contentStyle={{ borderRadius: 6, border: "1px solid #e2e8f0", fontSize: 12 }}
                    />
                    <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                      {data.reasons.map((r) => (
                        <Cell key={r.key} fill={REASON_COLORS[r.key] || "#334155"} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Verbatim feedback */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Quote className="w-4 h-4 text-slate-500" />
              Recent feedback (verbatim)
            </CardTitle>
            <CardDescription>
              The last 5 free-text notes owners left when cancelling. Read these first.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {loading ? (
              <p className="text-sm text-slate-400 text-center py-4">Loading…</p>
            ) : !data?.recent_feedback?.length ? (
              <p className="text-sm text-slate-400 text-center py-6" data-testid="churn-no-feedback">
                No free-text feedback in this window yet.
              </p>
            ) : (
              data.recent_feedback.map((f, i) => (
                <div
                  key={i}
                  className="rounded-lg border border-slate-200 bg-slate-50/50 p-4"
                  data-testid={`churn-feedback-${i}`}
                >
                  <p className="text-sm text-[#0A192F] italic leading-relaxed">
                    &ldquo;{f.quote}&rdquo;
                  </p>
                  <div className="flex items-center gap-2 flex-wrap mt-2 text-xs text-slate-500">
                    <span
                      className="inline-flex items-center rounded-full px-2 py-0.5 text-white font-semibold uppercase tracking-wide text-[9px]"
                      style={{ background: REASON_COLORS[f.reason_key] || "#334155" }}
                    >
                      {f.reason_label}
                    </span>
                    {f.tenant_company && <span className="font-medium text-slate-600">{f.tenant_company}</span>}
                    {f.plan && <span className="capitalize">· {f.plan}</span>}
                    {f.date && <span>· {new Date(f.date).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</span>}
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminChurnInsights;
